import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, ParamMap } from '@angular/router'
import { PagedResult } from '@ftr/contracts/api/core/response/PagedResult'
import { PageMetadata } from '@ftr/contracts/type/shared'
import { ApiResult, ArrayUtils, DestroySubscribers, RemoteData, tapData } from '@ftr/foundation'
import { SharedRoutingParams } from '@ftr/routing-paths'
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs'

const DEFAULT_PAGE = 1

/*
 * A generic list component which exposes a page of entities and a total
 * count of entities with the implemented datasource
 */
@Component({ template: '', standalone: true })
export class ListComponent<T> extends DestroySubscribers implements OnInit {
  meta?: PageMetadata
  isLoading = true

  protected debounceTime = 20
  private readonly entitiesSubject = new BehaviorSubject<RemoteData<PagedResult<T>>>(RemoteData.loading())
  private fetch = new Subject<void>()

  readonly entities = this.entitiesSubject.pipe(tapData(data => (this.meta = data.meta)))

  constructor(protected readonly route: ActivatedRoute) {
    super()
  }

  ngOnInit(): void {
    // setup fetch subscription
    this.fetch
      .pipe(
        switchMap(() => this.getEntitiesObservable(this.route.paramMap, this.getPage())),
        tap(remote => (this.isLoading = remote.isLoading())),
        takeUntil(this.finalize),
      )
      .subscribe(remote => this.entitiesSubject.next(remote))

    // trigger first call for fetch
    this.populateEntities()
  }

  populateEntities(): void {
    setTimeout(() => this.fetch.next())
  }

  getPage(): Observable<number> {
    return this.route.queryParamMap.pipe(map(params => Number(params.get(SharedRoutingParams.Page) || DEFAULT_PAGE)))
  }

  getEntitiesObservable(
    paramMapObservable: Observable<ParamMap>,
    pageObservable: Observable<number>,
  ): ApiResult<PagedResult<T>> {
    if (!this.getEntities) {
      throw new Error('ListComponent must either implement getEntities or override getEntitiesObservable')
    }
    return combineLatest([paramMapObservable, pageObservable]).pipe(
      distinctUntilChanged(ArrayUtils.shallowEquals),
      switchMap(([paramMap, page]) => {
        return this.getEntities!(paramMap, page)
      }),
    )
  }

  getEntities?(paramMap: ParamMap, page: number): ApiResult<PagedResult<T>>
}
